"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ERROR_CODE, getHttpClient } from "@repo/api";
import { User } from "@repo/types";
import { UserAPI } from "@repo/api/user";
import { userKeys } from "@repo/query/keys";
import { toast } from "@heroui/react";

export interface UserManager {
    saveSessionData: (token: string, expire: number) => void;
    getSessionData: () => User.Session | undefined;
    /** 仅删除本地 session，不做跳转 / reload */
    removeSession: () => void;
}

function isAuthExpiredStatus(status: unknown): boolean {
    return status === ERROR_CODE.NEED_LOGIN || status === ERROR_CODE.TOKEN_EMPTY;
}

/** 当前登录用户信息很少变化，5 分钟内切回窗口不重新请求；写资料后由 refreshUserInfo 主动刷新 */
const ME_STALE_TIME = 5 * 60_000;

/** http 层对非 0 status 会 reject，成功时 status 恒为 0 */
const fetchMe = () => UserAPI.getUserInfo().then((r) => r.data);

interface AuthContextType {
    isAuthenticated: boolean | undefined;
    userInfo: User.Info | null;
    login: (token: string, expire: number) => Promise<void>;
    logout: () => Promise<void>;
    refreshUserInfo: () => Promise<void>;
    loading: boolean;
    isLoginModalOpen: boolean;
    setIsLoginModalOpen: (isOpen: boolean) => void;
}

interface AuthProviderProps {
    children: React.ReactNode;
    userManager: UserManager;
    /** 登录态被清除时的回调（主动登出 / 4000 / 4001）；查询缓存已由 Provider 自行清空，这里放应用层的其它清理 */
    onLogout?: () => void;
    /** 主动登出时先调用的后端注销接口（如 /auth/signout）；失败不影响本地登出。webview 的登录态归 App 所有，不要传 */
    signOut?: () => Promise<unknown>;
}

export const AuthContext = createContext<AuthContextType>({
    isAuthenticated: undefined,
    userInfo: null,
    login: async () => { },
    logout: async () => { },
    refreshUserInfo: async () => { },
    loading: true,
    isLoginModalOpen: false,
    setIsLoginModalOpen: () => { },
});

/**
 * 登录态 Provider。必须挂在 QueryClientProvider 之内：
 * 当前用户信息走 `userKeys.me()` 查询，登出 / 登录失效时整个 QueryClient 一起清空，避免上个账号的数据残留。
 */
export function AuthProvider({ children, userManager, onLogout, signOut }: AuthProviderProps) {
    const queryClient = useQueryClient();
    // 本地是否持有 session；没有 session 就不发 /user/info，isAuthenticated 直接为 false
    const [hasSession, setHasSession] = useState<boolean>(() => Boolean(userManager.getSessionData()));
    const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false); // 登录弹窗控制

    // onLogout 通过 ref 读取，避免调用方传内联函数时让 clearAuthState 反复变化
    const onLogoutRef = useRef(onLogout);
    useEffect(() => {
        onLogoutRef.current = onLogout;
    }, [onLogout]);

    const clearAuthState = useCallback(() => {
        userManager.removeSession();
        setHasSession(false);
        queryClient.clear();
        onLogoutRef.current?.();
    }, [userManager, queryClient]);

    // HTTP 层 4000/4001 时同步清 React 登录态
    useEffect(() => {
        try {
            getHttpClient().setLogoutCallback(clearAuthState);
        } catch {
            // http 尚未初始化时忽略
        }
    }, [clearAuthState]);

    const meQuery = useQuery({
        queryKey: userKeys.me(),
        queryFn: fetchMe,
        enabled: hasSession,
        staleTime: ME_STALE_TIME,
    });

    // 首次拉取失败的处理：token 失效（4000 / 4001）由 http 层的 logoutCallback 清登录态，这里只需把 isAuthenticated 置 false；
    // 网络错误 / 500 / 其它业务错误保留 token，仍视为已登录，仅提示
    const meErrorStatus = (meQuery.error as { status?: number } | null)?.status;
    const meAuthExpired = meQuery.isError && isAuthExpiredStatus(meErrorStatus);
    useEffect(() => {
        if (!meQuery.error || isAuthExpiredStatus(meErrorStatus)) return;
        console.error("获取用户信息失败:", meQuery.error);
        const msg = (meQuery.error as { msg?: string } | null)?.msg;
        toast.danger(msg || "网络异常，请稍后再试");
    }, [meQuery.error, meErrorStatus]);

    const userInfo = meQuery.data ?? null;
    const loading = hasSession && meQuery.isPending;
    let isAuthenticated: boolean | undefined;
    if (!hasSession || meAuthExpired) {
        isAuthenticated = false;
    } else if (meQuery.isSuccess || meQuery.isError) {
        isAuthenticated = true;
    } else {
        isAuthenticated = undefined; // 仍在首次加载
    }

    const login = async (token: string, expire: number) => {
        try {
            userManager.saveSessionData(token, expire);
            // 强制拉一次最新用户信息写进缓存，再打开 me 查询
            await queryClient.fetchQuery({ queryKey: userKeys.me(), queryFn: fetchMe, staleTime: 0 });
            setHasSession(true);
            setIsLoginModalOpen(false);
        } catch (error) {
            // 校验用户信息失败时清掉刚写入的脏 token
            userManager.removeSession();
            setHasSession(false);
            queryClient.removeQueries({ queryKey: userKeys.me() });
            console.error("登录失败:", error);
            toast.danger("登录失败，请重试");
            throw error;
        }
    };

    const logout = async () => {
        // 先带着当前 token 通知后端注销本机会话，再清本地登录态
        if (signOut && userManager.getSessionData()) {
            try {
                await signOut();
            } catch (error) {
                console.error("后端退出登录失败:", error);
            }
        }
        clearAuthState();
    };

    const refreshUserInfo = async () => {
        const session = userManager.getSessionData();
        if (!session) {
            setHasSession(false);
            queryClient.removeQueries({ queryKey: userKeys.me() });
            return;
        }
        try {
            await queryClient.fetchQuery({ queryKey: userKeys.me(), queryFn: fetchMe, staleTime: 0 });
            setHasSession(true);
        } catch (error: unknown) {
            const status = (error as { status?: number } | null)?.status;
            if (isAuthExpiredStatus(status)) {
                clearAuthState();
                return;
            }
            console.error("刷新用户信息失败:", error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                userInfo,
                login,
                logout,
                refreshUserInfo,
                loading,
                isLoginModalOpen,
                setIsLoginModalOpen,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
